import React, { useEffect, useState } from 'react';
import { Card, Form, Table, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { useClientRepricing } from '../utils/clientPrices';
import UNIDADES_MEDIDA from '../data/unidadesMedida';
import { PRODUCT_UNIT_CODES, DEFAULT_UNIT_CODE } from '../data/productUnits';
import Loading from './Loading';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const emptyItem = () => ({ product: '', sku: '', description: '', quantity: 1, unitPrice: 0, itbisRate: 0.18, unidadMedida: DEFAULT_UNIT_CODE });

// New conduce (delivery note) / edit a pending one. It can carry prices or leave them out.
const ConduceBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [showPrices, setShowPrices] = useState(false);
  const [exento, setExento] = useState(false);
  const [number, setNumber] = useState('');
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error(err));
    API.get('/products').then(res => setProducts(res.data)).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (!editing) return;
    API.get(`/conduces/${id}`).then(({ data }) => {
      if (data.status !== 'pending') { setError('Un conduce entregado no se puede modificar.'); setLoading(false); return; }
      setNumber(data.number);
      setClientId(data.client?._id || '');
      setDeliveryAddress(data.deliveryAddress || '');
      setReference(data.reference || '');
      setNotes(data.notes || '');
      setShowPrices(!!data.showPrices);
      const allExempt = data.items.length > 0 && data.items.every(it => it.itbisExento);
      setExento(allExempt);
      setItems(data.items.map(it => ({ product: it.product || '', itbisExento: allExempt ? false : !!it.itbisExento, sku: it.sku || '', description: it.description, quantity: it.quantity, unitPrice: it.unitPrice, itbisRate: it.itbisRate ?? 0.18, unidadMedida: it.unidadMedida ?? DEFAULT_UNIT_CODE })));
      setLoading(false);
    }).catch(() => { setError('No se pudo cargar el conduce.'); setLoading(false); });
  }, [editing, id]);

  const selectedClient = clients.find(c => c._id === clientId);

  // The client deal (acuerdo) this conduce will be deducted from, if the client has one open (backend services/clientDeals.js).
  const [dealHint, setDealHint] = useState(null);
  useEffect(() => {
    setDealHint(null);
    if (!clientId) return;
    API.get('/conduces/deal-hint', { params: { client: clientId } }).then(r => setDealHint(r.data.deal)).catch(() => {});
  }, [clientId]);

  const { priceNote, repriceFor } = useClientRepricing(items, setItems);

  const chooseClient = (c) => {
    setClientId(c._id);
    repriceFor(c._id, c.name); // lines added before the client was chosen get this client's special prices
    if (!deliveryAddress.trim()) setDeliveryAddress(c.address || ''); // start from the client's address; it can be changed
  };

  const updateItem = (index, patch) => setItems(prev => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const handleProductChange = async (index, productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) { updateItem(index, { product: '' }); return; }
    let unitPrice = product.price;
    if (clientId) {
      try {
        const { data } = await API.get('/client-prices/resolve', { params: { client: clientId, product: productId } });
        unitPrice = data.price;
      } catch (err) { console.error('Error resolving client price:', err); }
    }
    updateItem(index, { product: productId, sku: product.sku || '', description: product.name, unitPrice, priceEdited: false, itbisRate: product.itbisRate, unidadMedida: product.unidadMedida ?? DEFAULT_UNIT_CODE });
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // No ITBIS on a line when the whole conduce is exempt, the line was saved exempt, or its product is exempt.
  const productExento = (item) => !!products.find(p => p._id === item.product)?.itbisExento;
  const lineExento = (item) => exento || !!item.itbisExento || productExento(item);

  const totals = items.reduce((acc, item) => {
    const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
    acc.subtotal += lineTotal;
    if (!lineExento(item)) acc.itbis += lineTotal * (item.itbisRate ?? 0.18);
    return acc;
  }, { subtotal: 0, itbis: 0 });

  // The units a line can use: the ones we sell in, plus its own if it came in with another.
  const unitOptions = (current) => UNIDADES_MEDIDA.filter(u => PRODUCT_UNIT_CODES.includes(u.code) || u.code === Number(current));

  const handleSubmit = async () => {
    setError('');
    if (!clientId) { setError('Selecciona un cliente.'); return; }
    const validItems = items.filter(it => it.description && Number(it.quantity) > 0);
    if (validItems.length === 0) { setError('Agrega al menos una línea con descripción y cantidad.'); return; }

    setSaving(true);
    try {
      const payload = {
        client: clientId, showPrices, deliveryAddress, reference, notes,
        items: validItems.map(it => ({
          product: it.product || undefined, sku: it.sku, description: it.description, quantity: Number(it.quantity),
          unitPrice: showPrices ? Number(it.unitPrice) : 0, itbisExento: showPrices && lineExento(it), itbisRate: it.itbisRate, unidadMedida: it.unidadMedida,
        })),
      };
      if (editing) {
        await API.put(`/conduces/${id}`, payload);
        navigate('/conduces');
      } else {
        const { data } = await API.post('/conduces', payload);
        window.open(`/conduces/${data._id}/print`, '_blank');
        navigate('/conduces');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar el conduce.');
      setSaving(false);
    }
  };

  if (loading) return <Loading page label="Cargando conduce..." />;

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>{editing ? `Editar Conduce ${number}` : 'Nuevo Conduce'}</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <Card className="shadow-sm p-3 mb-3">
        <Row>
          <Col md={7}>
            <Form.Label>Cliente</Form.Label>
            <SearchSelect
              options={clients} value={clientId} getKey={c => c._id}
              getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
              onSelect={chooseClient} placeholder="Buscar cliente..." emptyLabel="Sin clientes coincidentes"
            />
            {selectedClient && (
              <div className="text-muted small mt-2">{selectedClient.email || 'Sin email'} · {selectedClient.phone || 'Sin teléfono'}</div>
            )}
            {dealHint && (
              <div className="small mt-2" style={{ color: 'var(--mm-cyan)' }}>
                <i className="bi bi-journal-check" /> Este conduce se descuenta del acuerdo "{dealHint.title}"
                {dealHint.balance != null && <> (saldo {new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(dealHint.balance)})</>}: no se factura.
              </div>
            )}
          </Col>
          <Col md={5}>
            <Form.Label>Referencia <span className="text-muted">(opcional)</span></Form.Label>
            <Form.Control maxLength={60} value={reference} onChange={e => setReference(e.target.value)} placeholder="Orden de compra, factura relacionada..." />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={7}>
            <Form.Label>Dirección de entrega</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={300} value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} placeholder="Se llena con la dirección del cliente; puedes cambiarla" />
          </Col>
          <Col md={5}>
            <Form.Label>Notas <span className="text-muted">(opcional)</span></Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Visibles en el conduce" />
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
          <Card.Title className="mb-0">Productos a entregar</Card.Title>
          <Form.Check
            type="checkbox" id="conduce-prices" label="Incluir precios en el conduce"
            checked={showPrices} onChange={e => setShowPrices(e.target.checked)}
          />
        </div>
        {!showPrices && <div className="small text-muted mb-2">Sin precios: el conduce solo lista lo que se entrega (producto, cantidad y unidad).</div>}
        {showPrices && priceNote && <div className="small mb-2" style={{ color: 'var(--mm-cyan)' }}>{priceNote}</div>}
        <Table responsive className="mb-2">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th style={{ minWidth: 140 }}>No. de Item</th>
              <th style={{ minWidth: 180 }}>Producto</th>
              <th style={{ minWidth: 180 }}>Descripción</th>
              <th style={{ width: 100 }}>Cantidad</th>
              <th style={{ minWidth: 150 }}>Unidad</th>
              {showPrices && <th style={{ width: 130 }}>Precio Unit.</th>}
              {showPrices && <th style={{ width: 120 }} className="text-end">Total</th>}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const lineTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
              return (
                <tr key={i}>
                  <td className="align-middle text-muted">{i + 1}</td>
                  <td>
                    <SearchSelect options={products} value={item.product} getKey={p => p._id} getLabel={p => p.sku || ''}
                      onSelect={p => handleProductChange(i, p._id)} placeholder="Buscar No. de Item..." emptyLabel="Sin productos coincidentes" />
                  </td>
                  <td>
                    <SearchSelect options={products} value={item.product} getKey={p => p._id} getLabel={p => p.name}
                      onSelect={p => handleProductChange(i, p._id)} placeholder="Buscar producto..." emptyLabel="Sin productos coincidentes" />
                  </td>
                  <td>
                    <Form.Control maxLength={200} value={item.description} onChange={e => updateItem(i, { description: e.target.value })} />
                  </td>
                  <td><Form.Control type="number" min="0" step="1" value={item.quantity} onChange={e => updateItem(i, { quantity: e.target.value })} /></td>
                  <td>
                    <Form.Select className="w-100" value={item.unidadMedida} onChange={e => updateItem(i, { unidadMedida: Number(e.target.value) })}>
                      {unitOptions(item.unidadMedida).map(u => <option key={u.code} value={u.code}>{u.name}</option>)}
                    </Form.Select>
                  </td>
                  {showPrices && <td><Form.Control type="number" min="0" step="0.01" value={item.unitPrice} onChange={e => updateItem(i, { unitPrice: e.target.value, priceEdited: true })} /></td>}
                  {showPrices && <td className="text-end align-middle">{money(lineTotal)}</td>}
                  <td className="align-middle">
                    <Button size="sm" variant="outline-danger" onClick={() => removeItem(i)} disabled={items.length === 1}><i className="bi bi-x" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={addItem}>+ Agregar Línea</Button>

        {showPrices && (
          <div className="d-flex justify-content-end mt-4">
            <div style={{ minWidth: 260 }}>
              <div className="d-flex justify-content-end mb-3">
                <Form.Check label="Conduce exento de ITBIS" checked={exento} onChange={e => setExento(e.target.checked)} />
              </div>
              <div className="small text-muted mb-3 text-end">Marcarlo exime todas las líneas. Los productos exentos (ej.: oxígeno medicinal) nunca llevan ITBIS.</div>
              <div className="d-flex justify-content-between mb-1"><span className="text-muted">Subtotal</span><span>{money(totals.subtotal)}</span></div>
              <div className="d-flex justify-content-between mb-2"><span className="text-muted">ITBIS</span><span>{money(totals.itbis)}</span></div>
              <hr style={{ borderColor: 'var(--mm-line)' }} />
              <div className="d-flex justify-content-between">
                <span style={{ fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>Total</span>
                <span style={{ fontWeight: 700, fontSize: '1.3rem', color: 'var(--mm-cyan)' }}>{money(totals.subtotal + totals.itbis)}</span>
              </div>
            </div>
          </div>
        )}
      </Card>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" onClick={() => navigate('/conduces')}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear Conduce'}</Button>
      </div>
    </div>
  );
};

export default ConduceBuilder;
